import { api, unwrap, type APIResponse } from "./client";

export interface SpamProbeQueueCounts {
  queued: number;
  in_progress: number;
  completed: number;
  clean: number;
  spam: number;
  inconclusive: number;
  replaced: number;
  sold_off: number;
  pending_replace: number;
}

export interface SpamProbeHandsetSlot {
  line_id: number;
  device_id: number;
  phone: string;
  carrier_name: string;
  carrier_family: string;
  device_name: string;
  model: string | null;
  last_seen_at: string | null;
  state: string;
  current_did: string | null;
  current_tenant: string | null;
  calls_completed: number;
  verdicts_received: number;
  consecutive_timeouts: number;
  updated_at?: string | null;
}

export interface SpamProbeTenantSummary {
  usr_id: number;
  username: string;
  total_items: number;
  pending_items: number;
  clean_items: number;
  spam_items: number;
  inconclusive_items: number;
  replaced_items: number;
  sold_off_items: number;
}

export interface SpamProbeRunSummary {
  run_id: number;
  trigger_source: string;
  status: string;
  lookback_hours: number;
  min_rejects: number;
  pickup_deficit_pct: number;
  min_calls: number;
  usr_id_filter: number | null;
  did_group_id_filter: number | null;
  auto_replace: boolean;
  immediate_selloff: boolean;
  total_enqueued: number;
  completed_count: number;
  clean_count: number;
  spam_count: number;
  inconclusive_count: number;
  replaced_count: number;
  sold_off_count: number;
  notes: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface SpamProbeQueueStatus {
  runner_active: boolean;
  quiet_window_now: boolean;
  runner_state: Record<string, unknown> | null;
  counts: SpamProbeQueueCounts;
  eta_seconds: number;
  handset_slots: SpamProbeHandsetSlot[];
  by_tenant: SpamProbeTenantSummary[];
  recent_runs: SpamProbeRunSummary[];
}

export interface SpamProbeQueueItem {
  queue_id: number;
  run_id: number | null;
  did_id: number;
  phone: string;
  usr_id: number;
  username: string | null;
  did_group_id: number | null;
  total_calls: number;
  answered_calls: number;
  reject_603_21_count: number;
  pickup_rate: number;
  pickup_pct: number;
  peer_pickup_rate: number;
  peer_pickup_pct: number;
  pickup_delta: number;
  pickup_delta_pct: number;
  priority: number;
  reason: string | null;
  status: string;
  stage: number;
  attempts: number;
  max_attempts: number;
  assigned_line_id: number | null;
  assigned_phone: string | null;
  assigned_carrier: string | null;
  ami_hangup_cause: number | null;
  final_outcome: string | null;
  verdict_label: string | null;
  verdict_id: number | null;
  auto_replace: boolean;
  immediate_selloff: boolean;
  replace_status: string;
  replacement_phone: string | null;
  selloff_status: string;
  sold_off_at: string | null;
  error_message: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string | null;
}

export interface QuestionableDidCandidate {
  did_id: number;
  phone: string;
  usr_id: number;
  username: string;
  did_group_id: number | null;
  total_calls: number;
  answered_calls: number;
  reject_603_21_count: number;
  pickup_rate: number;
  pickup_pct: number;
  peer_pickup_rate: number;
  peer_pickup_pct: number;
  pickup_delta: number;
  pickup_delta_pct: number;
  priority: number;
  reason: string;
}

export interface QuestionableDidCandidatesResponse {
  scan: {
    lookback_hours: number;
    min_rejects: number;
    pickup_deficit_pct: number;
    min_calls: number;
    clean_cooldown_hours: number;
    fleet_calls: number;
    fleet_declines: number;
    fleet_decline_pct: number;
    storm_tripped: boolean;
    total_candidates: number;
    by_tenant: Array<{
      usr_id: number;
      username: string;
      candidate_count: number;
      peer_pickup_pct: number;
      max_reject_603_21: number;
    }>;
  };
  candidates: QuestionableDidCandidate[];
}

export interface QueueEnqueuePayload {
  lookback_hours?: number;
  min_rejects?: number;
  pickup_deficit_pct?: number;
  min_calls?: number;
  clean_cooldown_hours?: number;
  usr_id?: number | null;
  did_group_id?: number | null;
  force_reprobe?: boolean;
  auto_start?: boolean;
  auto_replace?: boolean;
  immediate_selloff?: boolean;
  respect_quiet_hours?: boolean;
  ring_seconds?: number;
  max_attempts?: number;
  limit?: number;
}

export interface QueueEnqueueResponse {
  run_id: number | null;
  enqueued: number;
  skipped_already_queued: number;
  storm_tripped: boolean;
  runner_started: boolean;
  scan: QuestionableDidCandidatesResponse["scan"];
}

export type QueueControlAction =
  | "start"
  | "resume"
  | "pause"
  | "cancel"
  | "retry_inconclusive"
  | "drain_replacements";

export async function getSpamProbeQueueStatus(params?: {
  usr_id?: number;
}): Promise<SpamProbeQueueStatus> {
  const { data } = await api.get<APIResponse<SpamProbeQueueStatus>>(
    "/spam-telemetry/queue/status",
    { params }
  );
  return unwrap(data);
}

export async function listSpamProbeQueueItems(params?: {
  run_id?: number;
  usr_id?: number;
  status?: string;
  outcome?: string;
  replace_status?: string;
  limit?: number;
}): Promise<{ items: SpamProbeQueueItem[]; count: number }> {
  const { data } = await api.get<
    APIResponse<{ items: SpamProbeQueueItem[]; count: number }>
  >("/spam-telemetry/queue/items", { params });
  return unwrap(data);
}

export async function getQuestionableDidCandidates(params?: {
  lookback_hours?: number;
  min_rejects?: number;
  pickup_deficit_pct?: number;
  min_calls?: number;
  clean_cooldown_hours?: number;
  usr_id?: number;
  did_group_id?: number;
  force_reprobe?: boolean;
  limit?: number;
}): Promise<QuestionableDidCandidatesResponse> {
  const { data } = await api.get<APIResponse<QuestionableDidCandidatesResponse>>(
    "/spam-telemetry/queue/candidates",
    { params }
  );
  return unwrap(data);
}

export async function enqueueSpamProbeQueue(
  payload: QueueEnqueuePayload
): Promise<QueueEnqueueResponse> {
  const { data } = await api.post<APIResponse<QueueEnqueueResponse>>(
    "/spam-telemetry/queue/enqueue",
    payload
  );
  return unwrap(data);
}

export async function controlSpamProbeQueue(payload: {
  action: QueueControlAction;
  run_id?: number | null;
  respect_quiet_hours?: boolean;
}): Promise<Record<string, unknown>> {
  const { data } = await api.post<APIResponse<Record<string, unknown>>>(
    "/spam-telemetry/queue/control",
    payload
  );
  return unwrap(data);
}
